import { Spacing } from '@persona-id/design-tokens';
import { EditIcon, PersonaIcon } from '@persona-id/design-tokens/icons';
import { useCallback, VFC } from 'react';
import styled from 'styled-components';

import PlatformSchemaEditor from 'dashboard/components/PlatformSchemaEditor';
import JsonSchemaPreview from 'dashboard/components/WorkflowControlPanel/JsonSchemaPreview';
import ParamLabel from 'dashboard/components/WorkflowControlPanel/ParamLabel';
import StepConfigTypeCard from 'dashboard/components/WorkflowControlPanel/StepConfigTypeCard';
import TriggerAdvancedConfigurationPanel from 'dashboard/components/WorkflowControlPanel/TriggerAdvancedConfigurationPanel/TriggerAdvancedConfigurationPanel';
import TriggerLiveTest from 'dashboard/components/WorkflowControlPanel/TriggerLiveTest/TriggerLiveTest';
import useWorkflowError from 'dashboard/components/WorkflowControlPanel/useWorkflowError';
import CodeHighlight from 'dashboard/components-lib/CodeHighlight';
import { TriggerApi } from 'dashboard/models/trigger';
import { WorkflowError } from 'dashboard/models/workflow';
import { WorkflowRun } from 'dashboard/models/workflow-run';
import { WorkflowRunSimulated } from 'dashboard/models/workflow-run-simulated';
import Button from 'design-system/components/Button';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import { headingMixins } from 'design-system/mixins/text';
import useToggle from 'lib/hooks/useToggle';

const StyledCodeHighlight = styled(CodeHighlight)`
  border: 1px solid ${({ theme }) => theme.color.border.input};
  border-radius: ${({ theme }) => theme.element.borderRadius};
`;

const HorizontalContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-grow: 1;
  justify-content: space-between;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const SubTitle = styled.div`
  ${headingMixins.sm}
  padding-bottom: ${Spacing[2]};
`;

interface TriggerConfigApiProps {
  trigger: TriggerApi;
  editable: boolean;
  errors: WorkflowError[];
  workflowRun: WorkflowRun | WorkflowRunSimulated | null;
  workflowId: string;
  handleClose: () => void;
  updateTrigger: (newTrigger: TriggerApi) => void;
  isDirty?: boolean;
  isCurrentPublishedVersion?: boolean;
  workflowVersionExpiration: number;
  updateWorkflowVersionExpiration: (expiration: number) => void;
}

const TriggerConfigApi: VFC<TriggerConfigApiProps> = ({
  trigger,
  editable,
  errors,
  workflowRun,
  workflowId,
  handleClose,
  updateTrigger,
  isDirty = false,
  isCurrentPublishedVersion = true,
  workflowVersionExpiration,
  updateWorkflowVersionExpiration,
}) => {
  const jsonFieldSchema = trigger.attributes['param-schema'][0];

  const updateParamSchema = useCallback(
    (newJsonSchemaVal: string) => {
      updateTrigger({
        ...trigger,
        attributes: {
          'param-schema': [
            {
              ...jsonFieldSchema,
              config: {
                'json-schema': newJsonSchemaVal,
              },
            },
          ],
        },
      });
    },
    [updateTrigger, trigger, jsonFieldSchema],
  );

  const [
    isJsonSchemaEditorOpen,
    { disable: closeJsonSchemaEditor, toggle: toggleJsonSchemaEditor },
  ] = useToggle(false);

  const { renderErrorMessageFor } = useWorkflowError<keyof TriggerApi['attributes']>(errors);

  return (
    <>
      <Container>
        <HorizontalContainer>
          <SubTitle>Trigger Step</SubTitle>
        </HorizontalContainer>
        <Spacer height={2} />
        <StepConfigTypeCard icon={<PersonaIcon size={24} />} title='API Trigger' />
        <Spacer height={2} />
        <SubTitle>Configuration</SubTitle>
        {isJsonSchemaEditorOpen && (
          <PlatformSchemaEditor
            panelTitle='Parameter Schema'
            jsonSchema={JSON.parse(jsonFieldSchema.config['json-schema']) ?? {}}
            includePersonaObjects={false}
            templateOptionsByObjectKey={{}}
            onClosePanel={closeJsonSchemaEditor}
            updateJsonSchema={(newSchema) => {
              updateParamSchema(JSON.stringify(newSchema));
            }}
          />
        )}
        <ParamLabel
          labelText='Parameter Schema'
          htmlFor='trigger-config-api__edit-schema'
          buttons={[
            <Button
              key='edit'
              width='icon'
              variant='basic'
              disabled={!editable}
              onClick={toggleJsonSchemaEditor}
              id='trigger-config-api__edit-schema'
            >
              <EditIcon />
            </Button>,
          ]}
        />
        <JsonSchemaPreview
          id='trigger-config-api__return-schema-preview'
          jsonSchema={JSON.parse(jsonFieldSchema.config['json-schema'])?.['properties'] ?? {}}
          requiredFields={JSON.parse(jsonFieldSchema.config['json-schema'])?.['required'] ?? []}
          onClick={toggleJsonSchemaEditor}
        />
        {renderErrorMessageFor('param-schema')}
      </Container>
      {workflowRun && (
        <>
          <Spacer />
          <SubTitle>Output</SubTitle>
          <StyledCodeHighlight language='json' light>
            {JSON.stringify(
              workflowRun.attributes['initial-variables']['trigger-params']['fields'],
              null,
              2,
            )}
          </StyledCodeHighlight>
        </>
      )}
      <Spacer height={3} />
      <TriggerAdvancedConfigurationPanel
        editable={editable}
        workflowVersionExpiration={workflowVersionExpiration}
        updateWorkflowVersionExpiration={updateWorkflowVersionExpiration}
      />
      <Spacer height={3} />
      {!workflowRun && (
        <TriggerLiveTest
          apiEndpoint={`/api/v1/workflows/${workflowId}/trigger`}
          parameterJson={JSON.parse(jsonFieldSchema.config['json-schema'])?.['properties'] ?? {}}
          isDirty={isDirty}
          isCurrentPublishedVersion={isCurrentPublishedVersion}
        />
      )}
      <Spacer height={3} />
      <HStack equal={true}>
        <Button
          id='trigger-config-api__close-button'
          aria-label='Close panel'
          onClick={handleClose}
        >
          Close
        </Button>
      </HStack>
    </>
  );
};

export default TriggerConfigApi;
